// app/src/lib/snapping/snapEngine.ts
import type { SnapCandidate, SnapKind, SnapResult, SnapSettings, Vec3 } from './types';

export interface ResolveSnapArgs {
  cursorPx: [number, number];
  projectToPixels: (p: Vec3) => [number, number] | null;
  candidates: SnapCandidate[];
  surfaceHit: Vec3 | null;
  settings: SnapSettings;
  thresholdPx: number;
}

// Higher number = higher priority. surface is the lowest non-none.
const PRIORITY: Record<Exclude<SnapKind, 'none'>, number> = {
  vertex: 3,
  midpoint: 2,
  edge: 1,
  surface: 0,
};

const distPx = (a: [number, number], b: [number, number]) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** Nearest point (and its param t) on screen segment a->b to point p, in pixel space. */
function nearestOnSegmentPx(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): { t: number; px: [number, number] } {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return { t: 0, px: a };
  let t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return { t, px: [a[0] + abx * t, a[1] + aby * t] };
}

const lerp3 = (a: Vec3, b: Vec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/**
 * Resolve the best snap for the current pointer. Picks, among candidates within
 * the pixel threshold, the highest-priority kind; ties broken by smaller pixel
 * distance. Falls back to the raw surface hit, then to 'none'.
 */
export function resolveSnap(args: ResolveSnapArgs): SnapResult {
  const { cursorPx, projectToPixels, candidates, surfaceHit, settings, thresholdPx } = args;

  if (!settings.enabled) {
    return surfaceHit ? { kind: 'surface', position: surfaceHit } : { kind: 'none', position: [0, 0, 0] };
  }

  // Collect into an array rather than mutating a `let` from inside the closure:
  // TS can't narrow a closure-mutated `let` and would treat it as `never`.
  const matches: Array<{ result: SnapResult; pri: number; d: number }> = [];

  const consider = (kind: Exclude<SnapKind, 'none' | 'surface'>, world: Vec3, screenPx: [number, number], featureId: string) => {
    if (!settings.targets[kind]) return;
    const d = distPx(cursorPx, screenPx);
    if (d > thresholdPx) return;
    matches.push({ result: { kind, position: world, sourceFeatureId: featureId }, pri: PRIORITY[kind], d });
  };

  for (const c of candidates) {
    if (c.kind === 'vertex' || c.kind === 'midpoint') {
      const px = projectToPixels(c.position);
      if (px) consider(c.kind, c.position, px, c.featureId);
    } else {
      const pa = projectToPixels(c.a);
      const pb = projectToPixels(c.b);
      if (!pa || !pb) continue;
      const { t, px } = nearestOnSegmentPx(cursorPx, pa, pb);
      consider('edge', lerp3(c.a, c.b, t), px, c.featureId);
    }
  }

  if (matches.length > 0) {
    // Highest priority wins; ties broken by smaller pixel distance.
    matches.sort((a, b) => b.pri - a.pri || a.d - b.d);
    return matches[0].result;
  }
  return surfaceHit
    ? { kind: 'surface', position: surfaceHit }
    : { kind: 'none', position: [0, 0, 0] };
}
